import { SyncPair } from './sync-pair.service'
import { Arn } from 'src/utils/types'
import { ResourceType } from 'src/utils/resource-types'
import { Observable } from 'rxjs'
import { SyncSource } from './sync-source.service'
import { map } from 'rxjs/operators'
import { Application } from './application.service'
import {
  ApplicationIssueDto,
  ApplicationIssueScopeDto,
  Issue,
  SyncPairIssueDto,
  SyncPairIssueScopeDto,
  SyncSourceIssueDto,
  SyncSourceIssueScopeDto,
} from 'src/app/services/dtos'
import { environment } from 'src/environments/environment'

export class BaseIssue {
  // This is deliberately named differently from the subtypes so the type system
  // can tell if we're accidentally pointing here.
  static baseDisplayName = 'Issue'

  constructor(
    public message: string,
    public title?: string
  ) {}

  get issueType(): string {
    return this.issueTypeDisplayString
  }

  get issueTypeDisplayString() {
    if (!(this.title === undefined || this.title === null)) {
      return this.title
    } else {
      return BaseIssue.baseDisplayName
    }
  }
}

export class PersistentIssue extends BaseIssue {
  public static displayName = 'Notification'

  constructor(message: string) {
    super(message)
  }

  get issueTypeDisplayString() {
    return PersistentIssue.displayName
  }
}

export class DeleteFailedIssue extends BaseIssue {
  public static displayName = 'Delete Failed'

  constructor() {
    const message = `${environment.productName} was not able to successfully delete the application. Please try again or contact support if the issue persists.`
    const title = 'Failed to delete application'
    super(message, title)
  }
}

export class AcmCertificateNotFoundIssue extends BaseIssue {
  public static displayName = 'ACM Certificate Not Found'
  constructor(
    message: string,
    public sourceCertificateArn: string,
    public domain: string
  ) {
    super(message)
  }

  get issueTypeDisplayString() {
    return AcmCertificateNotFoundIssue.displayName
  }
}

export class ClientErrorIssue extends BaseIssue {
  public static displayName = 'Client Error'
  constructor(
    message: string,
    public errorCode: string,
    public errorMessage: string,
    public operation: string
  ) {
    super(message)
  }

  get issueTypeDisplayString() {
    return ClientErrorIssue.displayName
  }
}

export class QuotaIssue extends ClientErrorIssue {
  public static displayName = 'AWS Limit Reached'
  get issueTypeDisplayString() {
    return QuotaIssue.displayName
  }
}

export class MultipleMatchingImagesIssue extends BaseIssue {
  public static displayName = 'Multiple Matching Images'

  constructor(
    message: string,
    public matchCriteria: Map<string, Array<string>>
  ) {
    super(message)
  }

  get issueTypeDisplayString() {
    return MultipleMatchingImagesIssue.displayName
  }
}

export class MarketplaceAmiCantBeCopiedIssue extends BaseIssue {
  public static displayName = 'AWS Marketplace AMI'
  constructor(
    message: string,
    public productCodes: Array<string>,
    public matchCriteria: Map<string, Array<string>>
  ) {
    super(message)
  }

  get issueTypeDisplayString() {
    return MarketplaceAmiCantBeCopiedIssue.displayName
  }
}

export class MarketplaceInstanceCantBeCopiedIssue extends BaseIssue {
  public static displayName = 'AWS Marketplace Instance'
  get issueTypeDisplayString() {
    return MarketplaceInstanceCantBeCopiedIssue.displayName
  }
}

export class AmiStorageNotAccessibleIssue extends BaseIssue {
  public static displayName = 'AMI Storage Access'

  constructor(
    message: string,
    public matchCriteria: Map<string, Array<string>>
  ) {
    super(message)
  }

  get issueTypeDisplayString() {
    return AmiStorageNotAccessibleIssue.displayName
  }
}

export class UnhandledRouteTargetIssue extends BaseIssue {
  public static displayName = 'Unhandled Route Target'
  constructor(
    message: string,
    public routeTableArn: string,
    public target: string
  ) {
    super(message)
  }

  get issueTypeDisplayString() {
    return UnhandledRouteTargetIssue.displayName
  }
}

export class InfectedFiles {
  constructor(
    public malwareType: string,
    public totalFileCount: number,
    public files: Array<string>
  ) {}
}

export class MalwareScanIssue extends BaseIssue {
  public static issueType = 'MalwareScan'
  constructor(
    public scanStatus: string,
    public scanId: string,
    public description: string,
    public consoleLink: string,
    public infectedFiles: Array<InfectedFiles>
  ) {
    super(description)
  }

  get issueType() {
    return MalwareScanIssue.issueType
  }

  get issueTypeDisplayString() {
    if (this.scanStatus == 'infected') {
      return 'Infection Found During Malware Scan'
    } else {
      return 'Malware scan failed'
    }
  }
}

export class MarketplaceActionRequiredIssue extends BaseIssue {
  public static displayName = 'Unlicensed Marketplace Product'
  constructor(
    message: string,
    public marketplaceUrl?: string
  ) {
    super(message)
  }

  get issueTypeDisplayString() {
    return MarketplaceActionRequiredIssue.displayName
  }
}

export class RefreshClientErrorIssue extends ClientErrorIssue {
  public static displayName = 'Refresh Client Error'
  constructor(
    message: string,
    errorCode: string,
    errorMessage: string,
    operation: string,
    public resourceType: string
  ) {
    super(message, errorCode, errorMessage, operation)
  }

  get issueTypeDisplayString() {
    return RefreshClientErrorIssue.displayName
  }
}

export class KeyPairNotFoundIssue extends BaseIssue {
  public static displayName = 'Key Pair Not Found'
  constructor(public keyPairName: string) {
    super(
      `A key pair named '${keyPairName}' does not exist in the recovery environment. ` +
        `Arpio cannot replicate key pairs and other secrets, so you must create them manually.`
    )
  }

  get issueTypeDisplayString() {
    return KeyPairNotFoundIssue.displayName
  }
}

export class S3BucketReplicationAlreadyConfiguredIssue extends BaseIssue {
  public static displayName = 'Bucket Replication Already Enabled'
  constructor(public bucketName: string) {
    super(
      `Bucket replication is already enabled for this bucket (${bucketName}) and cannot be reconfigured by Arpio to ` +
        `an additional destination.  Contact Arpio support (support@arpio.io) for assistance.`
    )
  }

  get issueTypeDisplayString() {
    return S3BucketReplicationAlreadyConfiguredIssue.displayName
  }
}

// This issue is for when the user hasn't told Arpio how they want replication to work.
export class UnconfiguredS3BucketReplicationIssue extends BaseIssue {
  public static displayName = 'Configuration Required'
  constructor(
    public bucketName: string,
    message: string
  ) {
    super(message)
  }

  get issueTypeDisplayString() {
    return UnconfiguredS3BucketReplicationIssue.displayName
  }
}

export class BucketBackfillFailedIssue extends BaseIssue {
  public static displayName = 'Bucket Backfill Unsuccessful'
  constructor(
    public failedBucketReports: string[],
    public totalCopies: number,
    public failedCopies: number,
    message: string
  ) {
    super(message)
  }

  get issueTypeDisplayString() {
    return BucketBackfillFailedIssue.displayName
  }
}

// This issue is for when the user has chosen to manually configure replication.
export class S3BucketReplicationNotConfiguredIssue extends BaseIssue {
  public static displayName = 'Bucket Replication Configuration Needed'
  constructor(
    public sourceBucketName: string,
    public replicationIamRole: string,
    public targetBucketArn: string,
    public kmsKeyArn: string | null,
    message: string
  ) {
    super(message)
  }

  get issueTypeDisplayString() {
    return S3BucketReplicationNotConfiguredIssue.displayName
  }
}

export class S3RecoveryBucketNameConflictIssue extends BaseIssue {
  public static displayName = 'Recovery Bucket Name Conflict'
  constructor(
    public primaryBucketName: string,
    public recoveryBucketName,
    message: string
  ) {
    super(message)
  }

  get issueTypeDisplayString() {
    return S3RecoveryBucketNameConflictIssue.displayName
  }
}

export class UninstallDrsAgentIssue extends BaseIssue {
  public static displayName = 'Remove Elastic Disaster Recover Service'
  constructor(
    public sourceServerId: string,
    public ec2InstanceId: string
  ) {
    super(
      `Arpio is no longer using the Elastic Disaster Recovery service to replicate the EC2 instance ` +
        `with ID ${ec2InstanceId}.  At this time, you may disconnect and delete this instance from within the Elastic Disaster ` +
        `Recovery service console.`
    )
  }

  get issueTypeDisplayString(): string {
    return UninstallDrsAgentIssue.displayName
  }
}

export class FailbackResourceIssue extends BaseIssue {
  public static displayName = 'Cannot Failback to Resource'
  // return new FailbackResourceNotFoundIssue(issueDto.resourceArn, issueDto.resourceId, issueDto.resourceType);
  constructor(
    message: string,
    public resourceArn: Arn,
    public resourceId: string,
    public resourceType: ResourceType,
    public timestamp: Date
  ) {
    super(message)
  }

  get issueTypeDisplayString(): string {
    return FailbackResourceIssue.displayName
  }
}

// DEFUNCT.  The issues this class wraps have been replaced with DetailedIssues. This class
// can be removed when those issues are removed from the databases.
//
// This class does not correspond to an actual server issue type, but it allows us to
// unify the way we present several types of issues that can all be resolved
// by updating access templates.
export class SyncPairPermissionIssue extends BaseIssue {
  public static displayName = 'Insufficient Permissions'
  public static insufficientIamPermissionIssueDisplayName = 'Insufficient IAM Permissions'
  constructor(
    message: string,
    public operation?: string
  ) {
    super(message)
  }

  get issueTypeDisplayString() {
    if (this.isInsufficientIamPermissionIssue) {
      return SyncPairPermissionIssue.insufficientIamPermissionIssueDisplayName
    }
    return SyncPairPermissionIssue.displayName
  }

  get isInsufficientIamPermissionIssue() {
    const iamOperations = [
      'AddRoleToInstanceProfile',
      'AttachRolePolicy',
      'CreateInstanceProfile',
      'CreatePolicy',
      'CreatePolicyVersion',
      'CreateRole',
      // 'CreateServiceLinkedRole' won't happen with newer templates because we create
      // service linked roles in the delegate, which always has this permission.  If
      // We see this error in an issue, the solution is to update the access templates
      // to a version that does the work in the delegate.
      'DeleteInstanceProfile',
      'DeletePolicy',
      'DeletePolicyVersion',
      'DeleteRole',
      'DeleteRolePermissionsBoundary',
      'DeleteRolePolicy',
      'DetachRolePolicy',
      // 'PassRole' is not one of the restricted IAM permissions in newer templates.
      // It's always granted to the access role when listed for a template version.
      // If we get an error about it, the solution is to update the access templates
      // to one that includes the action.
      'PutRolePermissionsBoundary',
      'PutRolePolicy',
      'RemoveRoleFromInstanceProfile',
      'TagRole',
      'UntagRole',
      'UpdateAssumeRolePolicy',
      'UpdateRole',
    ]
    return !!this.operation && iamOperations.indexOf(this.operation) !== -1
  }
}

export class ResourcePolicyDeniedIssue extends BaseIssue {
  public static displayName = 'Access is explicitly denied by a resource policy'
  constructor(public message: string) {
    super(message)
  }

  get issueTypeDisplayString() {
    return ResourcePolicyDeniedIssue.displayName
  }
}

export class ServiceControlPolicyPermissionIssue extends BaseIssue {
  public static displayName = 'Access is explicitly denied by a Service Control Policy'
  constructor(public message: string) {
    super(message)
  }

  get issueTypeDisplayString() {
    return ServiceControlPolicyPermissionIssue.displayName
  }
}

export class DrsAgentNotFoundIssue extends BaseIssue {
  public static displayName = 'Elastic Disaster Recovery Agent Not Installed'
  constructor(
    message: string,
    public hardwareId: string,
    public usingSnapshots: boolean
  ) {
    super(message)
  }

  get issueTypeDisplayString() {
    return DrsAgentNotFoundIssue.displayName
  }
}

export class SubscriptionOverageIssue extends BaseIssue {
  public static displayName = 'Subscription Overage'
  constructor(message: string) {
    super(message)
  }

  get issueTypeDisplayString() {
    return SubscriptionOverageIssue.displayName
  }
}

export class EksClusterAccessDeniedIssue extends BaseIssue {
  public static displayName = 'Insufficient EKS Cluster Permissions'
  constructor(
    public message: string,
    public clusterName: string,
    public delegateRoleArn: string,
    public region: string,
    public originalMessage: string
  ) {
    super(message)
  }

  get issueTypeDisplayString() {
    return EksClusterAccessDeniedIssue.displayName
  }
}

export class EndpointInaccessibleIssue extends BaseIssue {
  public static displayName = 'AWS API Endpoint Inaccessible'
  constructor(
    public message: string,
    public endpointUrl: string
  ) {
    super(message)
  }

  get issueTypeDisplayString() {
    return EndpointInaccessibleIssue.displayName
  }
}

export class EksRequiredEndpointInaccessibleIssue extends BaseIssue {
  public static displayName = 'EKS required AWS API Endpoint Inaccessible'
  constructor(
    public message: string,
    public endpointUrl: string,
    public serviceName: string,
    public subnets: string[]
  ) {
    super(message)
  }

  get issueTypeDisplayString() {
    return EksRequiredEndpointInaccessibleIssue.displayName
  }
}

export interface SubnetMove {
  srcSubnetId: string
  tgtSubnetId: string
  oldAz: string
  newAz: string
}

export class NewTargetPlanRequiredIssue extends BaseIssue {
  public static displayName = 'New Recovery Subnet-AZ Mapping Required'
  constructor(
    public message: string,
    public subnetMoves: SubnetMove[]
  ) {
    super(message)
  }

  get issueTypeDisplayString() {
    return NewTargetPlanRequiredIssue.displayName
  }
}

// This issue is for when the user hasn't told Arpio how they want replication to work.
export class UnconfiguredAzureBlobContainerIssue extends BaseIssue {
  public static displayName = 'Blob Container Configuration Required'
  constructor(
    public storageAccountName: string,
    containerName: string,
    message: string
  ) {
    super(message)
  }

  get issueTypeDisplayString() {
    return UnconfiguredAzureBlobContainerIssue.displayName
  }
}
/**
 * Detailed Issue class
 */
export class DetailedIssue extends BaseIssue {
  public static displayName = 'Detailed Issue'
  public static issueType = 'detailedIssue'
  constructor(
    public message: string,
    public title?: string,
    public originalErrorMessage?: string, // The original error message that caused the issue.
    public additionalDetailsTitle?: string, // Title for link pointing to additional details
    public additionalDetailsUrl?: string, // URL for link pointing to additional details
    public promptToUpdateTemplates?: boolean,
    public scripts?: { [language: string]: string } // Scripts keyed by language (e.g. 'bash', 'powershell')
  ) {
    super(message, title)
  }

  get issueTypeDisplayString() {
    return this.title ?? DetailedIssue.displayName
  }
  get issueType() {
    return DetailedIssue.issueType
  }
}

export function adaptIssueDtoToIssue(issueDto: Issue) {
  switch (issueDto.type) {
    case 'acmCertificateNotFound': {
      return new AcmCertificateNotFoundIssue(issueDto.message, issueDto.sourceCertificateArn, issueDto.domainName)
    }
    case 'cantAssumeCustomerRole': {
      return new SyncPairPermissionIssue(issueDto.message)
    }
    case 'incompatibleAccessResources': {
      return new SyncPairPermissionIssue(issueDto.message)
    }
    case 'clientError': {
      return new ClientErrorIssue(issueDto.message, issueDto.errorCode, issueDto.errorMessage, issueDto.operation)
    }
    case 'permission': {
      return new SyncPairPermissionIssue(issueDto.message, issueDto.operation)
    }
    case 'refreshClientError': {
      return new RefreshClientErrorIssue(
        issueDto.message,
        issueDto.errorCode,
        issueDto.errorMessage,
        issueDto.operation,
        issueDto.resourceType
      )
    }
    case 'refreshPermission': {
      return new SyncPairPermissionIssue(issueDto.message, issueDto.operation)
    }
    case 'resourcePolicyDenied': {
      return new ResourcePolicyDeniedIssue(issueDto.message)
    }
    case 'scpAccessDenied': {
      return new ServiceControlPolicyPermissionIssue(issueDto.message)
    }
    case 'quota': {
      return new QuotaIssue(issueDto.message, issueDto.errorCode, issueDto.errorMessage, issueDto.operation)
    }
    case 'marketplaceAmiCantBeCopied': {
      return new MarketplaceAmiCantBeCopiedIssue(issueDto.message, issueDto.productCodes, issueDto.matchCriteria)
    }
    case 'multipleMatchingImages': {
      return new MultipleMatchingImagesIssue(issueDto.message, issueDto.matchCriteria)
    }
    case 'marketplaceInstanceCantBeCopied': {
      return new MarketplaceInstanceCantBeCopiedIssue(issueDto.message)
    }
    case 'amiStorageNotAccessible': {
      return new AmiStorageNotAccessibleIssue(issueDto.message, issueDto.matchCriteria)
    }
    case 'unhandledRouteTarget': {
      return new UnhandledRouteTargetIssue(issueDto.message, issueDto.routeTableArn, issueDto.target)
    }
    case 'malwareScanIssue': {
      return new MalwareScanIssue(
        issueDto.scanStatus,
        issueDto.scanId,
        issueDto.message,
        issueDto.consoleLink,
        issueDto.infectedFiles
      )
    }
    case 'marketplaceActionRequired': {
      return new MarketplaceActionRequiredIssue(issueDto.message, issueDto.marketplaceUrl)
    }
    case 'keyPairNotFound': {
      return new KeyPairNotFoundIssue(issueDto.keyPairName)
    }
    case 's3BucketReplicationAlreadyConfigured': {
      return new S3BucketReplicationAlreadyConfiguredIssue(issueDto.bucketName)
    }
    case 'unconfiguredS3BucketReplication': {
      return new UnconfiguredS3BucketReplicationIssue(issueDto.bucketName, issueDto.message)
    }
    case 'bucketBackfillFailed': {
      return new BucketBackfillFailedIssue(
        issueDto.failedBucketReports,
        issueDto.totalCopies,
        issueDto.failedCopies,
        issueDto.message
      )
    }
    case 's3BucketReplicationNotConfigured': {
      return new S3BucketReplicationNotConfiguredIssue(
        issueDto.sourceBucketName,
        issueDto.replicationRoleArn,
        issueDto.targetBucketArn,
        issueDto.kmsKeyArn || null,
        issueDto.message
      )
    }
    case 's3RecoveryBucketNameConflict': {
      return new S3RecoveryBucketNameConflictIssue(
        issueDto.primaryBucketName,
        issueDto.recoveryBucketName,
        issueDto.message
      )
    }
    case 'drsReplicationAgentNotFound': {
      return new DrsAgentNotFoundIssue(issueDto.message, issueDto.hardwareId, issueDto.usingSnapshots)
    }
    case 'drsUninstallAgent': {
      return new UninstallDrsAgentIssue(issueDto.sourceServerId, issueDto.ec2InstanceId)
    }
    case 'detailedIssue': {
      return new DetailedIssue(
        issueDto.message,
        issueDto.title,
        issueDto.originalErrorMessage,
        issueDto.additionalDetailsTitle,
        issueDto.additionalDetailsUrl,
        issueDto.promptToUpdateTemplates,
        issueDto.scripts
      )
    }
    case 'failbackResource': {
      return new FailbackResourceIssue(
        issueDto.message,
        issueDto.resourceArn,
        issueDto.resourceId,
        issueDto.resourceType,
        issueDto.timestamp
      )
    }
    case 'entitlementLimitsExceeded': {
      return new SubscriptionOverageIssue(issueDto.message)
    }
    case 'persistentIssue': {
      return new PersistentIssue(issueDto.message)
    }
    case 'eksClusterAccessDenied': {
      return new EksClusterAccessDeniedIssue(
        issueDto.message,
        issueDto.clusterName,
        issueDto.delegateRoleArn,
        issueDto.region,
        issueDto.originalMessage
      )
    }
    case 'endpointInaccessible': {
      return new EndpointInaccessibleIssue(issueDto.message, issueDto.endpointUrl)
    }
    case 'eksRequiredEndpointInaccessible': {
      return new EksRequiredEndpointInaccessibleIssue(
        issueDto.message,
        issueDto.endpointUrl,
        issueDto.endpointService,
        issueDto.subnets
      )
    }
    case 'newTargetPlanRequired': {
      return new NewTargetPlanRequiredIssue(issueDto.message, issueDto.subnetMoves)
    }
    case 'unconfiguredAzureBlobContainerIssue': {
      return new UnconfiguredAzureBlobContainerIssue(
        issueDto.storageAccountName,
        issueDto.containerName,
        issueDto.message
      )
    }
    default:
      if (typeof issueDto.title != 'undefined') {
        return new BaseIssue(issueDto.message, issueDto.title)
      }
      return new BaseIssue(issueDto.message)
  }
}

export function isPersistentIssue(issueDto: Issue) {
  switch (issueDto.type) {
    case 'bucketBackfillFailed':
      return true
    case 'drsUninstallAgent':
      return true
    case 'persistentIssue':
      return true
  }
  return false
}

export enum RaisedIssueScope {
  SCAN = 'scan',
  ENHANCE_FOR_RECOVERY_POINT = 'enhanceForRecoveryPoint',
  BACKUP = 'backup',
  RESTORE = 'restore',
  FAILBACK = 'failback',
  CLEANUP = 'cleanup',
}

function adaptSyncSourceIssueScopeDtoToRaisedIssueScope(ssScope: SyncSourceIssueScopeDto) {
  switch (ssScope) {
    case SyncSourceIssueScopeDto.ENHANCE_FOR_RECOVERY_POINT:
      return RaisedIssueScope.ENHANCE_FOR_RECOVERY_POINT
    case SyncSourceIssueScopeDto.REFRESH:
      return RaisedIssueScope.SCAN
    case SyncSourceIssueScopeDto.SCAN:
      return RaisedIssueScope.SCAN
  }
  throw new Error(`Unknown SyncSourceIssueScopeDto: ${ssScope}`)
}

function adaptSyncPairIssueScopeDtoToRaisedIssueScope(spScope: SyncPairIssueScopeDto) {
  switch (spScope) {
    case SyncPairIssueScopeDto.CLEANUP:
      return RaisedIssueScope.CLEANUP
    case SyncPairIssueScopeDto.FAILBACK:
      return RaisedIssueScope.FAILBACK
    case SyncPairIssueScopeDto.STAGING:
      return RaisedIssueScope.BACKUP
    case SyncPairIssueScopeDto.SYNC:
      return RaisedIssueScope.RESTORE
  }
  throw new Error(`Unknown SyncPairIssueScopeDto: ${spScope}`)
}

function adaptApplicationIssueScopeDtoToRaisedIssueScope(aScope: ApplicationIssueScopeDto) {
  switch (aScope) {
    case ApplicationIssueScopeDto.BACKUP:
      return RaisedIssueScope.BACKUP
    case ApplicationIssueScopeDto.CLEANUP:
      return RaisedIssueScope.CLEANUP
    case ApplicationIssueScopeDto.FAILBACK:
      return RaisedIssueScope.FAILBACK
    case ApplicationIssueScopeDto.RESTORE:
      return RaisedIssueScope.RESTORE
    case ApplicationIssueScopeDto.SCAN:
      return RaisedIssueScope.SCAN
    case ApplicationIssueScopeDto.ENHANCE_FOR_RECOVERY_POINT:
      return RaisedIssueScope.ENHANCE_FOR_RECOVERY_POINT
  }
}

export interface IssueDeleter {
  (): Observable<void>
}

export class RaisedIssue {
  constructor(
    public issueId: string,
    public arn: Arn,
    public scope: RaisedIssueScope,
    public issue: BaseIssue,
    // public issueDto: SyncSourceIssueDto | SyncPairIssueDto | ApplicationIssueDto, // don't know if I really need this anywhere...
    public deleter$: IssueDeleter,
    public timestamp?: Date
  ) {}
}

export function adaptSyncSourceIssueDtoToRaisedIssue(issueDto: SyncSourceIssueDto, syncSource: SyncSource) {
  let deleter$ = () => syncSource.deleteSyncSourceIssue$(issueDto.issueId, issueDto.scope).pipe(map(() => null))
  return new RaisedIssue(
    issueDto.issueId,
    issueDto.arn,
    adaptSyncSourceIssueScopeDtoToRaisedIssueScope(issueDto.scope),
    adaptIssueDtoToIssue(issueDto.issue),
    // issueDto,
    deleter$
  )
}

export function adaptSyncPairIssueDtoToRaisedIssue(issueDto: SyncPairIssueDto, syncPair: SyncPair) {
  let deleter$ = () => syncPair.deleteSyncPairIssue$(issueDto.issueId, issueDto.scope).pipe(map(() => null))
  return new RaisedIssue(
    issueDto.issueId,
    issueDto.arn,
    adaptSyncPairIssueScopeDtoToRaisedIssueScope(issueDto.scope),
    adaptIssueDtoToIssue(issueDto.issue),
    // issueDto,
    deleter$
  )
}

export function adaptApplicationIssueDtoToRaisedIssue(issueDto: ApplicationIssueDto, application: Application) {
  let deleter$ = () => application.deleteApplicationIssue$(issueDto.issueId, issueDto.scope).pipe(map(() => null))
  return new RaisedIssue(
    issueDto.issueId,
    issueDto.arn,
    adaptApplicationIssueScopeDtoToRaisedIssueScope(issueDto.scope),
    adaptIssueDtoToIssue(issueDto.issue),
    // issueDto,
    deleter$,
    issueDto.timestamp
  )
}
